import { useEffect, useRef } from 'react';

/**
 * Runs `callback` when the tab comes back to the front.
 *
 * `visibilitychange` covers switching tabs within a window, window `focus`
 * switching back from another window or app. One return usually fires both,
 * so a reveal within `minIntervalMs` of the last run is dropped. Used by the
 * billing page, the agent editor and the budget refresh: one listener pair
 * instead of a copy per place.
 */
export function useOnTabReveal(callback: () => void, minIntervalMs = 10_000): void {
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  });

  useEffect(() => {
    let lastRun = 0;
    const onReveal = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastRun < minIntervalMs) return;
      lastRun = now;
      callbackRef.current();
    };
    document.addEventListener('visibilitychange', onReveal);
    window.addEventListener('focus', onReveal);
    return () => {
      document.removeEventListener('visibilitychange', onReveal);
      window.removeEventListener('focus', onReveal);
    };
  }, [minIntervalMs]);
}
